<template>
  <div class="documents-metadata">
    <h4>{{ t('documentDetailView.metadata') }}</h4>

    <table-wrapper
      id="metadata-table"
      :fields="tableFields"
      :filter-display="undefined"
      :items="filteredAndSortedItems"
      :loading="!isInitialized"
      :paginator="false"
      :pt="{
        tableContainer: {
          style: {
            overflow: 'visible',
          },
        },
      }"
    >
      <template #cell(field)="{ data }">
        <template v-if="data">
          <div
            v-if="data.field === 'documentId' && isWorkspace && isDaisyDoc"
            class="flex gap-2"
          >
            {{ t(`contentTypeFields.${data.fieldDisplayName}`) }}
            <div
              v-tooltip.right="{
                value: t('documentDetailView.workspace.info'),
                pt: {
                  text: 'w-[20rem]',
                },
              }"
            >
              <font-awesome-icon :icon="['fas', 'circle-info']" />
            </div>
          </div>
          <div v-else>
            {{ t(`contentTypeFields.${data.fieldDisplayName}`) }}
          </div>
        </template>
      </template>

      <template #cell(value)="{ data }">
        <template v-if="data">
          <slot
            v-if="$slots[`value-${data.field}`]"
            :name="`value-${data.field}`"
            :data="data"
          />

          <template
            v-else-if="
              isNotAutoSet(data.field) &&
              isFieldEditable(data.field) &&
              canEditMetadata &&
              updateMetadataFunction &&
              documentMetadataAndFieldInfos
            "
          >
            <template v-if="isTaxonomyMultiField(data.field)">
              <div class="taxonomy-multi-field">
                <document-metadata-tag-input
                  :update-metadata-function="updateMetadataFunction"
                  :current-values="data.value"
                  :get-metadata-function="getMetadataAndFieldInfos"
                  :is-waiting-for-values="!isInitialized"
                  :metadata-field="data.field"
                  :possible-values="getPossibleValuesForField(data.field)"
                  :validation-function="validateMetadataForField(data.field)"
                  :loading="isFieldBusy(data.field)"
                  @document-data-changed="$emit('document-data-changed')"
                ></document-metadata-tag-input>
              </div>
            </template>

            <template v-else-if="isTaxonomyField(data.field)">
              <document-metadata-select
                :current-value="data.value"
                :get-metadata-function="getMetadataAndFieldInfos"
                :is-waiting-for-values="!isInitialized"
                :metadata-field="data.field"
                :possible-values="getPossibleValuesForField(data.field)"
                :validation-function="validateMetadataForField(data.field)"
                :update-metadata-function="updateMetadataFunction"
                :loading="isFieldBusy(data.field)"
                @document-data-changed="$emit('document-data-changed')"
              ></document-metadata-select>
            </template>

            <template v-else-if="isDateField(data.field)">
              <document-metadata-calendar-input
                :get-metadata-function="getMetadataAndFieldInfos"
                :initial-value="data.value"
                :metadata-field="data.field"
                :validation-function="validateMetadataForField(data.field)"
                :update-metadata-function="updateMetadataFunction"
                :loading="isFieldBusy(data.field)"
                @document-data-changed="$emit('document-data-changed')"
              ></document-metadata-calendar-input>
            </template>

            <template v-else>
              <document-metadata-free-text-input
                :get-metadata-function="getMetadataAndFieldInfos"
                :initial-value="data.value"
                :metadata-field="data.field"
                :validation-function="validateMetadataForField(data.field)"
                :update-metadata-function="updateMetadataFunction"
                :save-invalid="saveInvalid"
                :loading="isFieldBusy(data.field)"
                @document-data-changed="$emit('document-data-changed')"
              ></document-metadata-free-text-input>
            </template>
          </template>

          <template v-else>
            {{ data.value }}
          </template>
        </template>
      </template>
    </table-wrapper>

    <div class="table-bottom-panel">
      <vf-button
        v-if="metadata.length"
        :variant="buttonVariants.secondary"
        :size="buttonSizes.medium"
        @click="toggleIsShowingOnlyEditableFields"
      >
        {{
          isShowingOnlyEditableFields
            ? $t('documentDetailView.showAllMetadata')
            : $t('documentDetailView.showLessMetadata')
        }}
      </vf-button>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, computed, onMounted } from 'vue';
  import { useI18n } from 'vue-i18n';
  import DocumentMetadataCalendarInput from '@/components/DocumentMetadataCalendarInput/DocumentMetadataCalendarInput.vue';
  import DocumentMetadataFreeTextInput from '@/components/DocumentMetadataFreeTextInput/DocumentMetadataFreeTextInput.vue';
  import DocumentMetadataSelect from '@/components/DocumentMetadataSelect/DocumentMetadataSelect.vue';
  import DocumentMetadataTagInput from '@/components/DocumentMetadataTagInput/DocumentMetadataTagInput.vue';
  import {
    CHECK_BOT_ESTABLISHED_KKS,
    ContentTypeFieldType,
    ICommonMetadataValueAndFieldMetadata,
    KKS,
  } from 'types';
  import { TableFieldsBuilder } from '../TableWrapper';
  import { IMetadataEntry } from '@/views/DocumentDetailView/DocumentDetailView.types';
  import {
    DocumentsMetadataResponse,
    FetchMetadataFunction,
    UpdateMetadataFunction,
  } from './DocumentsMetadata.types';
  import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
  import { useRoute } from 'vue-router';
  import { Route } from '@/routes';

  const { t } = useI18n();
  const emit = defineEmits<{
    (e: 'document-data-changed'): void;
  }>();
  defineExpose({
    getMetadataAndFieldInfos,
  });

  const {
    canEditMetadata,
    fetchMetadataAndFieldInfos,
    updateMetadataFunction,
    saveInvalid = true,
    busyFields,
  } = defineProps<{
    fetchMetadataAndFieldInfos: FetchMetadataFunction;
    updateMetadataFunction?: UpdateMetadataFunction;
    busyFields?: string[];
    saveInvalid?: boolean;
    canEditMetadata: boolean;
  }>();

  const route = useRoute();

  if (canEditMetadata && !updateMetadataFunction) {
    throw new Error(
      'DocumentsMetadata requires updateMetadataFunction when metadata editing is enabled.',
    );
  }

  const documentMetadataAndFieldInfos = ref<
    DocumentsMetadataResponse | undefined
  >();
  const isInitialized = ref<boolean>(false);
  const isFetchingMetadataWithFieldInfos = ref<boolean>(false);
  const isShowingOnlyEditableFields = ref<boolean>(true);
  const metadata = ref<IMetadataEntry[]>([]);

  const tableFields = computed(() =>
    new TableFieldsBuilder<IMetadataEntry>({
      labelTranslationKey: 'documentUpload',
      sortable: false,
    })
      .addField('field', { width: '30%' })
      .addField('value', { width: '70%' })
      .build(),
  );

  const isWorkspace = computed(
    () => route.name === Route.WORKSPACE_DOCUMENT_DETAIL_VIEW,
  );

  const isDaisyDoc = computed(() => {
    let isDaisy = false;
    const originalDoc = metadata.value.find(
      (field) => field.field === 'originalDocumentId',
    );
    if (originalDoc?.value) {
      isDaisy = true;
    }
    return isDaisy;
  });

  async function getMetadataAndFieldInfos() {
    isFetchingMetadataWithFieldInfos.value = true;

    const response = await fetchMetadataAndFieldInfos();
    const fieldData = Array.isArray(response) ? response[0] : response;

    documentMetadataAndFieldInfos.value = fieldData;
    metadata.value = fieldData.metadata.map((data) => ({
      field: data.fieldName,
      fieldDisplayName: data.fieldDisplayName,
      value: data.fieldValue ?? '',
    }));
    isFetchingMetadataWithFieldInfos.value = false;
    isInitialized.value = true;
  }

  const filteredAndSortedItems = computed(() => {
    metadata.value = sortMetadata(metadata.value);
    if (isShowingOnlyEditableFields.value) {
      return metadata.value.filter((field) => isNotAutoSet(field.field));
    }
    return metadata.value;
  });

  function toggleIsShowingOnlyEditableFields(): void {
    isShowingOnlyEditableFields.value = !isShowingOnlyEditableFields.value;
  }

  function sortMetadata(metadata: IMetadataEntry[]): IMetadataEntry[] {
    const predefinedOrder = [
      'Title',
      'Revision',
      KKS as string,
      CHECK_BOT_ESTABLISHED_KKS as string,
      'FolderNumber',
      'DocumentClass',
      'DocumentClassContent',
      'CreateDate',
      'ReleaseDate',
      'DocLanguage',
      'DocumentCreator',
      'CreatorDocumentNumber',
      'documentId',
      'originalDocumentId',
      'OriginalFilename',
      'ConfidentialClass',
      'DocStatus',
    ];

    return metadata.sort((a, b) => {
      const indexA = predefinedOrder.indexOf(a.fieldDisplayName);
      const indexB = predefinedOrder.indexOf(b.fieldDisplayName);

      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }

      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;

      return 0;
    });
  }

  function getFieldInfo(
    fieldName: string,
  ): ICommonMetadataValueAndFieldMetadata | undefined {
    return documentMetadataAndFieldInfos.value?.metadata.find(
      (fieldInfo) => fieldInfo.fieldName === fieldName,
    );
  }

  function getPossibleValuesForField(fieldName: string): string[] {
    return getFieldInfo(fieldName)?.possibleValues ?? [];
  }

  function isTaxonomyField(fieldName: string): boolean {
    return (
      getFieldInfo(fieldName)?.fieldType ===
      ContentTypeFieldType.TAXONOMY_FIELD_TYPE
    );
  }

  function isTaxonomyMultiField(fieldName: string): boolean {
    return (
      getFieldInfo(fieldName)?.fieldType ===
      ContentTypeFieldType.TAXONOMY_FIELD_TYPE_MULTI
    );
  }

  function isDateField(fieldName: string): boolean {
    return (
      getFieldInfo(fieldName)?.fieldType === ContentTypeFieldType.DATE_TIME
    );
  }

  function validateMetadataForField(
    fieldName: string | Date,
  ): (value?: string | Date) => boolean {
    const fieldInfo = documentMetadataAndFieldInfos.value?.metadata.find(
      (field) => field.fieldName === fieldName,
    );
    const isFieldRequired = fieldInfo?.required;

    if (fieldInfo === undefined) {
      throw Error(`Could not get field info for metadata field ${fieldName}`);
    }

    return (value) => !isFieldRequired || Boolean(value);
  }

  function isFieldEditable(fieldName: string): boolean {
    const field = getFieldInfo(fieldName);
    return !field?.readonly;
  }

  function isNotAutoSet(fieldName: string): boolean {
    const field = getFieldInfo(fieldName);
    return !field?.isAutoSet;
  }

  function isFieldBusy(fieldName: string): boolean {
    return busyFields?.includes(fieldName) ?? false;
  }

  onMounted(getMetadataAndFieldInfos);
</script>

<style scoped lang="scss">
  .documents-metadata {
    width: 100%;

    #metadata-table {
      width: 100%;

      .taxonomy-multi-field {
        display: flex;
        align-items: center;
        column-gap: 1rem;
      }
    }

    .table-bottom-panel {
      margin-top: 1.5rem;
      @include flex-center;
    }
  }
</style>
